"use client"

import { useState, useMemo } from "react"
import { Search, Check, ArrowRight } from "lucide-react"
import {
    Sheet,
    SheetContent,
    SheetHeader,
    SheetTitle,
    SheetDescription,
} from "@/components/ui/sheet"
import { cn } from "@/core/lib/utils"
import { useDictionary } from "@/core/hooks/use-dictionary"

interface ProductColor {
    product_id: string
    color_code: string
    color_code_2?: string
    color_title: string
}

interface MaterialSelectorProps {
    label: string
    options: ProductColor[]
    selectedId?: string
    productSlug: string
    onSelect?: (opt: ProductColor) => void
}

function ColorSwatch({
    color1,
    color2,
    size = "md",
    className,
}: {
    color1: string
    color2?: string
    size?: "sm" | "md" | "lg"
    className?: string
}) {
    const sizeMap = { sm: "w-9 h-9", md: "w-12 h-12", lg: "w-16 h-16" }

    if (color2) {
        // Split circle: left half = color1, right half = color2
        return (
            <div
                className={cn("rounded-full border-2 border-primary-900/10 shrink-0 overflow-hidden relative shadow-sm", sizeMap[size], className)}
            >
                <div
                    className="absolute inset-0"
                    style={{
                        background: `linear-gradient(135deg, ${color1} 50%, ${color2} 50%)`,
                    }}
                />
                {/* Divider line */}
                <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                    <div className="w-full h-px bg-background/30 rotate-[135deg]" />
                </div>
            </div>
        )
    }

    return (
        <div
            className={cn("rounded-full border-2 border-primary-900/10 shrink-0 shadow-sm", sizeMap[size], className)}
            style={{ backgroundColor: color1 }}
        />
    )
}

export default function MaterialSelector({
    label,
    options,
    selectedId,
    productSlug,
    onSelect,
}: MaterialSelectorProps) {
    const { dictionary } = useDictionary()
    const [isOpen, setIsOpen] = useState(false)
    const [searchTerm, setSearchTerm] = useState("")

    const selected = options.find((o) => o.product_id === selectedId) || options[0]

    const filtered = useMemo(() => {
        if (!searchTerm.trim()) return options
        const term = searchTerm.toLowerCase()
        return options.filter((o) => o.color_title.toLowerCase().includes(term))
    }, [options, searchTerm])

    if (options.length === 0) return null


    return (
        <>
            {/* Section */}
            <div className="flex flex-col gap-3.5">
                {/* Label row with selected name */}
                <div className="flex items-baseline gap-2">
                    <span className="text-xs text-primary-900">
                        {label}
                    </span>
                    {selected && (
                        <>
                            <span className="text-primary-600/40">|</span>
                            <span className="text-xs text-primary-600">{selected.color_title}</span>
                        </>
                    )}
                </div>

                {/* Inline swatches row */}
                <div className="flex items-center gap-2.5 flex-wrap">
                    {options.slice(0, 6).map((opt) => {
                        const isSelected = selectedId === opt.product_id
                        return (
                            <button
                                key={opt.product_id}
                                onClick={() => onSelect?.(opt)}
                                title={opt.color_title}
                                className={cn(
                                    "relative transition-all duration-200 hover:scale-105 bg-transparent p-0.5 rounded-full",
                                    isSelected
                                        ? "ring-2 ring-primary-900 ring-offset-2 ring-offset-background"
                                        : "ring-1 ring-transparent hover:ring-primary-900/20",
                                )}
                            >
                                <ColorSwatch
                                    color1={opt.color_code}
                                    color2={opt.color_code_2}
                                    size="sm"
                                />
                            </button>
                        )
                    })}

                    {/* "View All" button to open sheet */}
                    {options.length > 6 && (
                        <button
                            onClick={() => setIsOpen(true)}
                            className="h-9 px-4 flex items-center gap-1.5 text-xs text-primary-600 hover:text-primary-900 border border-primary-300/80 hover:border-primary-700/40 rounded-full transition-all duration-200 bg-transparent"
                        >
                            +{options.length - 6}
                            <ArrowRight className="w-3 h-3 rtl:rotate-180" />
                        </button>
                    )}
                </div>
            </div>

            {/* Sheet sidebar for browsing all */}
            <Sheet open={isOpen} onOpenChange={(open) => { setIsOpen(open); if (!open) setSearchTerm("") }}>
                <SheetContent side="right" className="w-full sm:max-w-md p-0 flex flex-col">
                    <SheetHeader className="px-6 pt-6 pb-4 border-b border-border/50 shrink-0">
                        <SheetTitle className="text-base font-primary-bold">
                            {label}
                        </SheetTitle>


                        {/* Search bar */}
                        {options.length > 8 && (
                            <div className="flex items-center gap-2 mt-3 px-3 py-2.5 bg-secondary/50 border border-border/50">
                                <Search className="w-3.5 h-3.5 text-primary-600 shrink-0" />
                                <input
                                    type="text"
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    placeholder={dictionary.common.search + "..."}
                                    className="flex-1 bg-transparent text-sm text-primary-900 placeholder:text-primary-600/60 outline-none"
                                />
                            </div>
                        )}
                    </SheetHeader>

                    {/* Options list */}
                    <div className="flex-1 overflow-y-auto px-4 py-4">
                        <div className="flex flex-col gap-1">
                            {filtered.map((opt) => {
                                const isSelected = selectedId === opt.product_id
                                return (
                                    <button
                                        key={opt.product_id}
                                        onClick={() => {
                                            onSelect?.(opt)
                                            setIsOpen(false)
                                            setSearchTerm("")
                                        }}
                                        className={cn(
                                            "flex items-center gap-4 px-4 py-3.5 transition-all duration-200 text-left w-full bg-transparent rounded-sm",
                                            isSelected
                                                ? "bg-primary-900/5 border border-primary-900/15"
                                                : "border border-transparent hover:bg-secondary/50",
                                        )}
                                    >
                                        <ColorSwatch
                                            color1={opt.color_code}
                                            color2={opt.color_code_2}
                                            size="lg"
                                        />
                                        <div className="flex flex-col gap-1 flex-1 min-w-0">
                                            <span className={cn(
                                                "text-sm truncate",
                                                isSelected ? "text-primary-900 font-medium" : "text-primary-900",
                                            )}>
                                                {opt.color_title}
                                            </span>
                                            {opt.color_code_2 && (
                                                <span className="text-xs text-primary-600 tracking-wide">Two Fabric</span>
                                            )}
                                        </div>
                                        {isSelected && (
                                            <span className="w-5 h-5 bg-primary-900 text-background rounded-full flex items-center justify-center shrink-0">
                                                <Check className="w-3 h-3" />
                                            </span>
                                        )}
                                    </button>
                                )
                            })}
                            {filtered.length === 0 && (
                                <p className="text-sm text-primary-600 text-center py-8">{dictionary.common.noResult}</p>
                            )}
                        </div>
                    </div>
                </SheetContent>
            </Sheet>
        </>
    )
}
